import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
    ArrowLeft, Search, Filter, Calendar, Phone, Mail, MapPin, Video, Stethoscope,
    Check, X as XIcon, Download, Pencil, Save, Send,
} from "lucide-react";
import { toast, Toaster } from "sonner";

/**
 * Admin → Appointments hub.
 *
 * One place to see every booking across the platform: which doctor is seeing
 * which patient, when, online or offline, paid or pending, plus the doctor's
 * cut (fee_doctor) and the platform's cut (fee_platform).
 *
 * Read-only for everyone but admin/editor (the backend gate enforces this).
 */
export default function AdminAppointments() {
    const [data, setData] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [filters, setFilters] = useState({
        q: "",
        doctor_id: "all",
        payment_status: "all",
        status: "all",
        mode: "all",
        from_date: "",
        to_date: "",
    });
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => {
            if (v && v !== "all") params[k] = v;
        });
        try {
            const { data: payload } = await api.get("/admin/appointments", { params });
            setData(payload);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load appointments");
        }
    }, [filters]);

    useEffect(() => {
        // Load the doctor list once for the filter dropdown
        api.get("/admin/doctors/registered").then(r => setDoctors(r.data || [])).catch(() => setDoctors([]));
    }, []);

    useEffect(() => {
        const id = setTimeout(load, 200); // debounce so typing in `q` doesn't slam the API
        return () => clearTimeout(id);
    }, [load]);

    const summary = data?.summary || {};
    const rows = data?.rows || [];
    const byDoctor = data?.by_doctor || [];

    const exportCsv = () => {
        if (!rows.length) return;
        const headers = ["Date", "Time", "Doctor", "Patient", "Email", "Phone", "Mode", "Payment", "Status", "Fee", "Doctor cut", "Platform cut", "Notes"];
        const csv = [headers, ...rows.map(r => [
            r.date, r.time_slot, r.doctor_name, r.user_name, r.user_email, r.user_phone, r.mode,
            r.payment_status, r.status, r.fee_total, r.fee_doctor, r.fee_platform, (r.notes || "").replace(/\n/g, " "),
        ])].map(row => row.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `appointments-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        toast.success("CSV exported");
    };

    return (
        <div data-testid="admin-appointments">
            <Toaster position="top-center" />
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700">Doctor consultations</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Appointments</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        Every consultation booked through the platform — search, filter, drill into details, mark complete or cancel.
                    </p>
                </div>
                <Button onClick={exportCsv} variant="outline" className="rounded-none border-stone-300" data-testid="appt-export">
                    <Download className="w-4 h-4 mr-2" />Export CSV
                </Button>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="appt-kpis">
                <KPI label="Total bookings" value={summary.total || 0} />
                <KPI label="Paid" value={summary.paid || 0} accent="emerald" />
                <KPI label="Pending payment" value={summary.pending || 0} accent="amber" />
                <KPI label="Upcoming today" value={summary.upcoming_today || 0} accent="sky" />
                <KPI label="Fees collected" value={formatINR(summary.fee_total || 0)} />
                <KPI label="Doctor share" value={formatINR(summary.fee_doctor || 0)} accent="stone" />
                <KPI label="Platform share" value={formatINR(summary.fee_platform || 0)} accent="emerald" />
                <KPI label="Cancelled" value={summary.cancelled || 0} accent="rose" />
            </div>

            {/* Filters */}
            <div className="bg-white border border-stone-200 p-4 mb-4 grid lg:grid-cols-7 gap-3" data-testid="appt-filters">
                <div className="lg:col-span-2 relative">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400 pointer-events-none" />
                    <Input
                        placeholder="Search doctor, patient, email, note…"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        className="rounded-none border-stone-300 pl-9"
                        data-testid="appt-search"
                    />
                </div>
                <div>
                    <Select value={filters.doctor_id} onValueChange={(v) => setFilters((f) => ({ ...f, doctor_id: v }))}>
                        <SelectTrigger className="rounded-none border-stone-300" data-testid="appt-doctor-filter"><SelectValue placeholder="Any doctor" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any doctor</SelectItem>
                            {doctors.map((d) => (
                                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Select value={filters.payment_status} onValueChange={(v) => setFilters((f) => ({ ...f, payment_status: v }))}>
                        <SelectTrigger className="rounded-none border-stone-300" data-testid="appt-payment-filter"><SelectValue placeholder="Payment" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any payment</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="refunded">Refunded</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                        <SelectTrigger className="rounded-none border-stone-300" data-testid="appt-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any status</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Select value={filters.mode} onValueChange={(v) => setFilters((f) => ({ ...f, mode: v }))}>
                        <SelectTrigger className="rounded-none border-stone-300" data-testid="appt-mode-filter"><SelectValue placeholder="Mode" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Any mode</SelectItem>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="offline">Clinic</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">From date</Label>
                        <Input type="date" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} className="rounded-none border-stone-300 mt-1" data-testid="appt-from-date" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">To date</Label>
                        <Input type="date" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} className="rounded-none border-stone-300 mt-1" data-testid="appt-to-date" />
                    </div>
                </div>
            </div>

            {/* Top doctors by bookings */}
            {byDoctor.length > 0 && (
                <div className="bg-white border border-stone-200 p-4 mb-4">
                    <div className="label-eyebrow text-stone-500 mb-3 flex items-center gap-2"><Stethoscope className="w-3 h-3" />Top doctors in this view</div>
                    <div className="flex flex-wrap gap-2">
                        {byDoctor.slice(0, 8).map((d) => (
                            <button
                                key={d.doctor_id}
                                onClick={() => setFilters((f) => ({ ...f, doctor_id: d.doctor_id }))}
                                className="border border-stone-200 px-3 py-2 text-xs hover:bg-sky-50 hover:border-sky-300 text-left"
                                data-testid={`appt-doctor-chip-${d.doctor_id}`}
                            >
                                <div className="font-medium text-stone-900">{d.doctor_name}</div>
                                <div className="text-stone-500 mt-0.5">{d.count} booking{d.count === 1 ? "" : "s"} · {formatINR(d.fee_total)}{d.paid > 0 && ` · ${d.paid} paid`}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-left text-[11px] uppercase tracking-widest text-stone-500">
                            <th className="p-3">Date · Time</th>
                            <th className="p-3">Doctor</th>
                            <th className="p-3">Patient</th>
                            <th className="p-3">Mode</th>
                            <th className="p-3">Payment</th>
                            <th className="p-3">Status</th>
                            <th className="p-3 text-right">Fee</th>
                            <th className="p-3 w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={8} className="text-center text-stone-500 py-10" data-testid="appt-empty">No appointments match the filters.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} className="border-t border-stone-100 hover:bg-sky-50/40 cursor-pointer" onClick={() => setOpenId(r.id)} data-testid={`appt-row-${r.id}`}>
                                <td className="p-3 whitespace-nowrap">
                                    <div className="font-mono text-stone-900">{r.date}</div>
                                    <div className="text-xs text-stone-500">{r.time_slot}</div>
                                </td>
                                <td className="p-3">
                                    <div className="font-medium text-stone-900">{r.doctor_name}</div>
                                </td>
                                <td className="p-3">
                                    <div className="font-medium text-stone-900">{r.user_name || "—"}</div>
                                    <div className="text-xs text-stone-500">{r.user_email}{r.user_phone ? ` · ${r.user_phone}` : ""}</div>
                                </td>
                                <td className="p-3">
                                    <ModeBadge mode={r.mode} />
                                </td>
                                <td className="p-3"><PaymentBadge value={r.payment_status} /></td>
                                <td className="p-3"><StatusBadge value={r.status} /></td>
                                <td className="p-3 text-right">
                                    <div className="font-medium">{formatINR(r.fee_total)}</div>
                                    <div className="text-[10px] text-stone-500">Dr {formatINR(r.fee_doctor)} · Plat {formatINR(r.fee_platform)}</div>
                                </td>
                                <td className="p-3 text-right">
                                    <Pencil className="w-4 h-4 text-sky-700 inline" />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {openId && (
                <AppointmentDrawer id={openId} onClose={() => setOpenId(null)} onSaved={() => { setOpenId(null); load(); }} />
            )}
        </div>
    );
}

function KPI({ label, value, accent }) {
    const tone = ({
        emerald: "border-emerald-200 text-emerald-700",
        amber: "border-amber-200 text-amber-700",
        rose: "border-rose-200 text-rose-700",
        sky: "border-sky-200 text-sky-700",
        stone: "border-stone-200 text-stone-700",
    })[accent] || "border-stone-200 text-stone-900";
    return (
        <div className={`bg-white border ${tone} p-3`}>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className="mt-1 text-lg font-medium">{value}</div>
        </div>
    );
}

function ModeBadge({ mode }) {
    if (mode === "online") return <span className="inline-flex items-center gap-1 text-xs text-sky-700"><Video className="w-3.5 h-3.5" />Online</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-stone-700"><MapPin className="w-3.5 h-3.5" />Clinic</span>;
}

function PaymentBadge({ value }) {
    if (!value) return <span className="text-stone-400 text-xs">—</span>;
    const map = {
        paid: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Paid" },
        pending: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
        refunded: { cls: "bg-stone-100 text-stone-700 border-stone-200", label: "Refunded" },
        failed: { cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Failed" },
    };
    const e = map[value] || { cls: "bg-stone-100", label: value };
    return <span className={`px-2 py-0.5 text-[11px] border ${e.cls}`}>{e.label}</span>;
}

function StatusBadge({ value }) {
    if (!value) return <span className="text-stone-400 text-xs">—</span>;
    const map = {
        booked: { cls: "bg-sky-100 text-sky-800 border-sky-200", label: "Booked" },
        completed: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Completed" },
        cancelled: { cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Cancelled" },
    };
    const e = map[value] || { cls: "bg-stone-100", label: value };
    return <span className={`px-2 py-0.5 text-[11px] border ${e.cls}`}>{e.label}</span>;
}

function AppointmentDrawer({ id, onClose, onSaved }) {
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [editing, setEditing] = useState({ status: "", payment_status: "", notes: "" });

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get(`/admin/appointments/${id}`);
            setDetail(data);
            setEditing({
                status: data.appointment.status,
                payment_status: data.appointment.payment_status,
                notes: data.appointment.notes || "",
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load appointment");
        }
    }, [id]);

    useEffect(() => { refresh(); }, [refresh]);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/admin/appointments/${id}`, editing);
            toast.success("Appointment updated");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const sendToDoctor = async () => {
        if (!confirm("Send the consultation brief (patient name, contact, note) to the doctor now via WhatsApp + push + email?")) return;
        setNotifying(true);
        try {
            const { data } = await api.post(`/admin/appointments/${id}/notify-doctor`, { include_phone: true, extra_note: "" });
            const sentChannels = Object.entries(data?.channels || {})
                .filter(([_, v]) => v?.sent || v?.account?.push?.sent || v?.account?.whatsapp?.sent || v?.account?.email?.sent)
                .map(([k]) => k);
            toast.success(sentChannels.length > 0
                ? `Sent via ${sentChannels.join(", ")}`
                : "Sent — check the doctor's contact channels");
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send");
        } finally {
            setNotifying(false);
        }
    };

    if (!detail) return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="rounded-none max-w-md">
                {/* sr-only DialogTitle satisfies the Radix a11y contract while we wait for the fetch */}
                <DialogHeader>
                    <DialogTitle className="sr-only">Loading appointment</DialogTitle>
                </DialogHeader>
                <div className="text-sm text-stone-500 py-8 text-center">Loading appointment…</div>
            </DialogContent>
        </Dialog>
    );

    const a = detail.appointment;
    const doc = detail.doctor;
    const pat = detail.patient;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="rounded-none max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="appt-drawer">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        <button onClick={onClose} className="text-sky-700 mr-2 align-middle"><ArrowLeft className="w-4 h-4 inline" /></button>
                        Appointment · <span className="text-stone-500 text-base font-sans">{a.date} {a.time_slot}</span>
                    </DialogTitle>
                </DialogHeader>

                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    {/* Doctor card */}
                    <section className="border border-stone-200 p-4 bg-white">
                        <div className="label-eyebrow text-sky-700 flex items-center gap-1.5"><Stethoscope className="w-3 h-3" />Doctor</div>
                        <div className="font-serif text-xl mt-2">{doc.name}</div>
                        <div className="text-xs text-stone-500">{doc.qualification} · {doc.specialization}</div>
                        {doc.phone && <div className="text-xs text-stone-700 mt-2 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{doc.phone}</div>}
                        {doc.email && <div className="text-xs text-stone-700 mt-1 flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{doc.email}</div>}
                        {doc.clinic_name && <div className="text-xs text-stone-700 mt-2"><div className="font-medium">{doc.clinic_name}</div><div className="text-stone-500">{[doc.clinic_address, doc.city, doc.state].filter(Boolean).join(", ")}</div></div>}
                        {doc.status && doc.status !== "approved" && <Badge variant="outline" className="rounded-none mt-2 text-xs">{doc.status}</Badge>}
                    </section>

                    {/* Patient card */}
                    <section className="border border-stone-200 p-4 bg-white">
                        <div className="label-eyebrow text-stone-700">Patient</div>
                        <div className="font-serif text-xl mt-2">{pat.name || "—"}</div>
                        <div className="text-xs text-stone-700 mt-2 flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{pat.email}</div>
                        {pat.phone && <div className="text-xs text-stone-700 mt-1 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{pat.phone}</div>}
                        {pat.address && <div className="text-xs text-stone-700 mt-2"><div>{pat.address}</div><div className="text-stone-500">{[pat.city, pat.state].filter(Boolean).join(", ")}</div></div>}
                    </section>
                </div>

                {/* Appointment details */}
                <section className="border border-stone-200 bg-white mt-4 p-4">
                    <div className="label-eyebrow text-stone-700">Visit details</div>
                    <div className="grid sm:grid-cols-3 gap-3 mt-3">
                        <DetailRow icon={Calendar} label="Date & time" value={`${a.date} · ${a.time_slot}`} />
                        <DetailRow icon={a.mode === "online" ? Video : MapPin} label="Mode" value={a.mode === "online" ? "Online video call" : "Clinic visit"} />
                        <DetailRow label="Booked on" value={(a.created_at || "").slice(0, 16).replace("T", " ")} />
                        <DetailRow label="Consultation fee" value={formatINR(a.fee_total)} accent="stone" />
                        <DetailRow label="Doctor share" value={formatINR(a.fee_doctor)} accent="emerald" />
                        <DetailRow label="Platform share" value={formatINR(a.fee_platform)} accent="sky" />
                    </div>
                    {a.paid_at && <div className="text-xs text-emerald-700 mt-2">✓ Paid on {(a.paid_at || "").slice(0, 16).replace("T", " ")}</div>}
                </section>

                {/* Patient note */}
                {(a.notes || editing.notes) && (
                    <section className="border border-stone-200 bg-amber-50 mt-4 p-4">
                        <div className="label-eyebrow text-amber-700">Patient note</div>
                        <pre className="whitespace-pre-wrap font-sans text-sm text-stone-800 mt-2">{a.notes || ""}</pre>
                    </section>
                )}

                {/* Admin actions */}
                <section className="border border-sky-200 bg-sky-50/40 mt-4 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="label-eyebrow text-sky-700">Admin actions</div>
                        <div className="flex items-center gap-2">
                            {a.doctor_notified_at && (
                                <span className="text-[11px] text-emerald-700 flex items-center gap-1">
                                    <Check className="w-3 h-3" />Doctor briefed {(a.doctor_notified_at || "").slice(0, 16).replace("T", " ")}
                                </span>
                            )}
                            <Button
                                onClick={sendToDoctor}
                                disabled={notifying}
                                variant="outline"
                                size="sm"
                                className="rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                data-testid="appt-send-to-doctor"
                            >
                                <Send className="w-3.5 h-3.5 mr-2" />
                                {notifying ? "Sending…" : (a.doctor_notified_at ? "Re-send brief" : "Send brief to doctor")}
                            </Button>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 mt-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Status</Label>
                            <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="appt-edit-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="booked">Booked</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Payment</Label>
                            <Select value={editing.payment_status} onValueChange={(v) => setEditing({ ...editing, payment_status: v })}>
                                <SelectTrigger className="rounded-none border-stone-300" data-testid="appt-edit-payment"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="paid">Paid</SelectItem>
                                    <SelectItem value="refunded">Refunded</SelectItem>
                                    <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Internal note (visible to patient too)</Label>
                        <Textarea
                            value={editing.notes}
                            onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                            rows={3}
                            className="rounded-none border-stone-300 mt-1 text-sm"
                            data-testid="appt-edit-notes"
                        />
                    </div>
                    <div className="flex gap-2 mt-3 justify-end">
                        <Button onClick={onClose} variant="ghost" className="rounded-none" data-testid="appt-cancel">
                            <XIcon className="w-4 h-4 mr-2" />Cancel
                        </Button>
                        <Button onClick={save} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="appt-save">
                            <Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </section>
            </DialogContent>
        </Dialog>
    );
}

function DetailRow({ icon: Icon, label, value, accent }) {
    const tone = ({
        emerald: "text-emerald-700",
        sky: "text-sky-700",
        stone: "text-stone-900",
    })[accent] || "text-stone-900";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />}{label}</div>
            <div className={`mt-1 text-sm font-medium ${tone}`}>{value || "—"}</div>
        </div>
    );
}
